import { t } from 'i18next';

import { warningTypeColorMap } from 'core/constants';
import { Margin, Tooltip, WarningCell } from 'core/elements';
import { WarningType } from 'core/enums';
import { getPercentage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { PriceBasedOn } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { IQuotePart } from 'quotes/types';
import { Color } from 'theme/enums';

interface IProps {
  quotePart: IQuotePart;
  onMarginClick?: (quotePart: IQuotePart) => void;
  isPricingEditable?: ({ data }) => boolean;
  minimumMarginThreshold?: number;
}

const getMarginContent = (
  quotePart: IQuotePart,
  marginHasWarnings: boolean,
  warningType: WarningType,
  isPricingEditable?: ({ data }) => boolean,
  onMarginClick?: (quotePart: IQuotePart) => void,
  minimumMarginThreshold?: number
) => {
  const {
    computedCost,
    price,
    priceBasedOn,
    replacementStandardCost,
    externalReplacementStandardCost,
  } = quotePart;

  const isAlternateVersionEnabled = Boolean(quotePart.alternateVersionEnabled);

  const isReplacementMode = isAlternateVersionEnabled && priceBasedOn === PriceBasedOn.REPLACEMENT;
  const activeReplacementStandardCost = replacementStandardCost ?? externalReplacementStandardCost;
  const hasReplacementStandardCost =
    activeReplacementStandardCost != null && activeReplacementStandardCost > 0;

  // Replacement pricing selected but the standard cost has not been materialized yet
  // (it is now derived lazily at its need-points). Flag it instead of silently pricing
  // the margin off the quoted cost.
  const replacementCostUnavailable = isReplacementMode && !hasReplacementStandardCost;

  const marginCost =
    isReplacementMode && hasReplacementStandardCost ? activeReplacementStandardCost : computedCost;

  const margin = PricingHelper.computeGpm(marginCost, price);

  const editableMargin = isPricingEditable ? isPricingEditable({ data: quotePart }) : false;
  const marginBelowThreshold = margin != null && margin < minimumMarginThreshold;

  const getMarginColor = () => {
    if (marginHasWarnings) {
      return warningTypeColorMap?.[warningType];
    }
    if (replacementCostUnavailable || marginBelowThreshold) {
      return Color.WARNING;
    }

    return Color.PRIMARY;
  };

  const marginContent = (
    <Margin
      price={price}
      computedCost={marginCost}
      color={getMarginColor()}
      onClick={editableMargin ? () => onMarginClick(quotePart) : undefined}
      editable={editableMargin}
      label={isAlternateVersionEnabled ? t('labelPricingMargin') : t('labelMargin')}
    />
  );

  // Shared validation warnings already surface an icon via the outer WarningCell.
  if (marginHasWarnings) {
    return marginContent;
  }

  if (replacementCostUnavailable) {
    return (
      <Tooltip color={Color.WARNING} title={t('warningReplacementStandardCostUnavailable')}>
        {marginContent}
      </Tooltip>
    );
  }

  if (!marginBelowThreshold) {
    return marginContent;
  }

  return (
    <Tooltip
      color={Color.WARNING}
      title={t('warningMarginBelowMinimumThreshold', {
        minimum: getPercentage(minimumMarginThreshold),
      })}
    >
      {marginContent}
    </Tooltip>
  );
};

const MarginWithWarning = ({
  quotePart,
  onMarginClick,
  isPricingEditable,
  minimumMarginThreshold,
}: IProps) => {
  const { computedCost } = quotePart;

  if (!computedCost) {
    return null;
  }

  const marginHasWarnings = QuotePartHelper.marginHasWarnings(quotePart);
  const warningType = QuotePartHelper.marginWarningType(quotePart);

  return (
    <WarningCell
      content={getMarginContent(
        quotePart,
        marginHasWarnings,
        warningType,
        isPricingEditable,
        onMarginClick,
        minimumMarginThreshold
      )}
      showWarning={marginHasWarnings}
      type={warningType}
    />
  );
};

export default MarginWithWarning;
